import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Book, Search, Filter, Grid, List, Eye, Download, 
  FileText, Calendar, User, Tag, Clock, CheckCircle,
  AlertCircle, Loader, MoreVertical, ExternalLink,
  X, History, TrendingUp, Star, CalendarDays, BookOpen, Edit, Brain
} from 'lucide-react';
import './Bibliotheque.css';
import './accessibility.css';
import { apiJSON, apiFetch } from '../config/api';
import notificationService, { handleApiError } from '../utils/notificationService';
import { safeJsonParse } from '../utils/safeUtils';
import { useAppContext } from '../context/AppContext';
import KOReaderStatus from './KOReaderStatus';
import LoadingStates from './LoadingStates';
import VirtualizedBookList from './VirtualizedBookList';
import EditBookModal from './EditBookModal';
import BookCover from './BookCover';
import QuizView from './QuizView';

// Types minimaux (P1 : suppression de la directive ts-nocheck). Le livre provient
// du backend/AppContext (forme non figée) : index signature souple + champs usuels.
interface BookItem {
  book_id?: number | string;
  id?: number | string;
  title?: string;
  author?: string;
  status?: string;
  created_at?: number;
  tags_json?: string;
  [key: string]: any;
}

interface FilterValues {
  authors: any[];
  languages: any[];
  genres: any[];
  tags: any[];
  formats: any[];
  publishers: any[];
  difficulties: any[];
}

/**
 * État de la requête serveur. `idle` = aucune recherche lancée (on affiche la
 * bibliothèque du contexte) ; il est distinct d'un `success` à zéro élément,
 * qui doit rester visible tel quel (audit BIB-002).
 */
type SearchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; items: BookItem[]; total: number }
  | { status: 'error'; message: string };

/**
 * Correspondance filtre d'UI -> paramètre attendu par GET /api/books/search.
 * Le backend est en snake_case : envoyer `yearFrom`/`readStatus`/`fileType`
 * revenait à ne poser aucun filtre (audit BIB-011).
 */
const SEARCH_PARAM_NAMES: Record<string, string> = {
  language: 'language',
  series: 'series',
  yearFrom: 'year_from',
  yearTo: 'year_to',
  genre: 'genre',
  difficulty: 'difficulty',
  publisher: 'publisher',
  readStatus: 'read_status',
  rating: 'rating',
  fileType: 'file_type'
};

/** 'all' et '' signifient « pas de filtre » et ne doivent jamais être envoyés. */
const isMeaningfulFilter = (value: string) => value !== '' && value !== 'all';

/** Le backend plafonne per_page à 100. */
const SEARCH_PAGE_SIZE = 100;

const sameBookId = (a: unknown, b: unknown) => String(a) === String(b);

const Bibliotheque = () => {
  const navigate = useNavigate();
  // Le contexte expose `isLoading` : lire `loading` laissait l'écran de
  // chargement mort. `loadBooks` invalide la query ['books'] (rafraîchissement).
  const {
    books: contextBooks,
    isLoading: contextLoading,
    error: contextError,
    loadBooks,
  } = useAppContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'cards', 'timeline'
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [authorFilter, setAuthorFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [formatFilter, setFormatFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Nouveaux états pour la recherche avancée
  const [searchFilters, setSearchFilters] = useState({
    language: 'all',
    series: '',
    yearFrom: '',
    yearTo: '',
    genre: 'all',
    difficulty: 'all',
    publisher: '',
    notes: '',
    readStatus: 'all', // 'all', 'read', 'unread', 'reading'
    rating: 'all', // 'all', '1-2', '3-4', '5'
    fileType: 'all' // 'all', 'pdf', 'epub', 'txt'
  });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchState, setSearchState] = useState<SearchState>({ status: 'idle' });
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce pour la recherche
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Incrément à chaque requête : une réponse tardive d'une recherche abandonnée
  // ne doit plus écraser l'état courant.
  const searchRequestIdRef = useRef(0);
  // Jeton incrémenté par le bouton « Réessayer » pour relancer la même requête.
  const [retryToken, setRetryToken] = useState(0);

  // Effectuer la recherche quand les critères changent
  useEffect(() => {
    const trimmedQuery = debouncedQuery.trim();
    const hasActiveFilter = Object.values(searchFilters).some(isMeaningfulFilter);

    // Aucun critère : on repasse sur la bibliothèque complète du contexte.
    if (!trimmedQuery && !hasActiveFilter) {
      searchRequestIdRef.current += 1;
      setSearchState({ status: 'idle' });
      return;
    }

    const requestId = ++searchRequestIdRef.current;
    setSearchState({ status: 'loading' });

    (async () => {
      try {
        const searchParams = new URLSearchParams({
          query: trimmedQuery,
          per_page: String(SEARCH_PAGE_SIZE)
        });
        Object.entries(searchFilters).forEach(([key, value]) => {
          const paramName = SEARCH_PARAM_NAMES[key];
          if (paramName && isMeaningfulFilter(value)) {
            searchParams.set(paramName, value);
          }
        });

        const response = await apiJSON(`books/search?${searchParams}`);
        if (requestId !== searchRequestIdRef.current) return; // réponse obsolète

        const items: BookItem[] = response.books || response.items || response.results || [];
        setSearchState({
          status: 'success',
          items,
          total: typeof response.total === 'number' ? response.total : items.length
        });
      } catch (error: any) {
        if (requestId !== searchRequestIdRef.current) return;
        console.error('Erreur recherche avancée:', error);
        // L'état est posé AVANT toute notification : si le notifieur lève, l'écran
        // ne doit pas rester bloqué sur « Recherche en cours ».
        setSearchState({
          status: 'error',
          message: error?.message || 'La recherche a échoué'
        });
        try {
          handleApiError(error, 'Recherche');
        } catch (notifyError) {
          console.warn('Notification d\'erreur impossible:', notifyError);
        }
      }
    })();
  }, [debouncedQuery, searchFilters, retryToken]);

  // La liste réellement affichée, exposée par une ref. handleBookAction est passé
  // au composant virtualisé : il doit rester stable tout en lisant la collection
  // courante. C'est cette closure figée sur un `contextBooks` encore vide qui
  // rendait « Modifier » muet après le chargement asynchrone (audit BIB-005).
  const visibleBooksRef = useRef<BookItem[]>([]);

  // Gestion de l'édition d'un livre
  const handleEditBook = useCallback((bookId: number | string | undefined) => {
    const book = visibleBooksRef.current.find((b: BookItem) => sameBookId(b.book_id, bookId));
    if (!book) {
      notificationService.showToast(
        '⚠️ Livre introuvable dans la liste affichée — rafraîchissez la bibliothèque',
        'warning'
      );
      return;
    }
    setEditingBook(book);
    setEditModalOpen(true);
  }, []);

  // Gestion des actions sur les livres
  const handleBookAction = useCallback(async (bookId: number | string | undefined, action: string) => {
    switch (action) {
      case 'read':
        window.open(`/lecteur?book_id=${bookId}&mode=internal`, '_blank');
        break;
      case 'reader':
        window.open(`/lecteur?book_id=${bookId}&mode=advanced`, '_blank');
        break;
      case 'ask':
        window.open(`/ia?book=${bookId}`, '_blank');
        break;
      case 'koreader':
        window.open(`/lecteur?book_id=${bookId}&mode=koreader`, '_blank');
        break;
      case 'folder':
        await handleOpenFolder(bookId);
        break;
      case 'export':
        await handleExportBook(bookId);
        break;
      case 'edit':
        handleEditBook(bookId);
        break;
      default:
        console.log('Action non reconnue:', action);
    }
  }, [handleEditBook]);

  // Gestion de la fermeture du modal d'édition
  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingBook(null);
  }, []);

  // Gestion du quiz
  const handleShowQuiz = useCallback(() => {
    // Sélectionner automatiquement les 3 premiers livres ou tous si moins de 3
    const booksToSelect = contextBooks.slice(0, 3);
    setSelectedBooksForQuiz(booksToSelect);
    setShowQuiz(true);
  }, [contextBooks]);

  const handleCloseQuiz = useCallback(() => {
    setShowQuiz(false);
    setSelectedBooksForQuiz([]);
  }, []);

  // Gestion de la sauvegarde après édition
  const handleSaveEdit = useCallback((updatedBook?: BookItem) => {
    // EditBookModal a déjà persisté (PUT /api/books/:id, échec => modal maintenu)
    // et affiché son propre toast de succès. Il reste à aligner l'affichage, qui
    // conservait les anciennes métadonnées jusqu'au rechargement (audit BIB-001).
    handleCloseEditModal();

    if (updatedBook) {
      // Fusion et non remplacement : la réponse du PUT ne renvoie qu'un
      // sous-ensemble des champs (ni status, ni couverture, ni created_at).
      setSearchState((prev) =>
        prev.status === 'success'
          ? {
              ...prev,
              items: prev.items.map((book) =>
                sameBookId(book.book_id, updatedBook.book_id)
                  ? { ...book, ...updatedBook }
                  : book
              )
            }
          : prev
      );
    }

    // Invalide la query ['books'] du contexte : la bibliothèque se recharge.
    loadBooks();
  }, [handleCloseEditModal, loadBooks]);

  // Gestion de la sélection de livre (compatibilité)
  const handleBookSelect = (book: BookItem) => {
    handleBookAction(book.book_id, 'read');
  };

  // Suggestions de recherche intelligentes
  const intelligentSuggestions = useMemo(() => {
    if (!Array.isArray(contextBooks) || !contextBooks.length) return [];

    const suggestions = new Set<string>();
    const query = debouncedQuery.toLowerCase();

    // Suggestions basées sur le contenu
    contextBooks.forEach((book: BookItem) => {
      // Titre
      if (book.title?.toLowerCase().includes(query)) {
        suggestions.add(`Titre: ${book.title}`);
      }
      
      // Auteur
      if (book.author?.toLowerCase().includes(query)) {
        suggestions.add(`Auteur: ${book.author}`);
      }
      
      // Série
      if (book.series?.toLowerCase().includes(query)) {
        suggestions.add(`Série: ${book.series}`);
      }
      
      // Tags
      if (book.tags) {
        const tags = Array.isArray(book.tags) ? book.tags : book.tags.split(',');
        tags.forEach((tag: string) => {
          if (tag.toLowerCase().includes(query)) {
            suggestions.add(`Tag: ${tag.trim()}`);
          }
        });
      }
      
      // Notes
      if (book.notes?.toLowerCase().includes(query)) {
        const words = book.notes.split(' ').filter((word: string) =>
          word.toLowerCase().includes(query) && word.length > 3
        );
        words.slice(0, 3).forEach((word: string) => {
          suggestions.add(`Note: ${word}`);
        });
      }
    });

    return Array.from(suggestions).slice(0, 10);
  }, [contextBooks, debouncedQuery]);

  // Nouveaux filtres avancés
  const [languageFilter, setLanguageFilter] = useState('all');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [yearFromFilter, setYearFromFilter] = useState('');
  const [yearToFilter, setYearToFilter] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [publisherFilter, setPublisherFilter] = useState('');
  const [notesFilter, setNotesFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // États pour les valeurs des filtres
  const [filterValues, setFilterValues] = useState<FilterValues>({
    authors: [],
    languages: [],
    genres: [],
    tags: [],
    formats: [],
    publishers: [],
    difficulties: []
  });
  const [loadingFilterValues, setLoadingFilterValues] = useState(false);

  // États pour le modal d'édition
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBook, setEditingBook] = useState<BookItem | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedBooksForQuiz, setSelectedBooksForQuiz] = useState<BookItem[]>([]);

  // États pour la virtualisation
  const [containerDimensions, setContainerDimensions] = useState({
    width: 1200,
    height: 600
  });
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Utilisation du service de notifications centralisé
  const notify = (type: string, message: string) => {
    notificationService.showToast(message, type);
  };

  // Charger les valeurs des filtres
  useEffect(() => {
    const loadFilterValues = async () => {
      setLoadingFilterValues(true);
      try {
        const response = await apiJSON('books/filters/values');
        setFilterValues(response);
      } catch (error) {
        console.error('Erreur chargement valeurs filtres:', error);
      } finally {
        setLoadingFilterValues(false);
      }
    };

    if (contextBooks.length > 0) {
      loadFilterValues();
    }
  }, [contextBooks]);

  // Mesurer les dimensions du conteneur pour la virtualisation
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerDimensions((previous) => ({
          // Une largeur nulle signifie « pas encore mis en page » (onglet masqué,
          // premier rendu) et non « large de 0 px » : on garde la précédente,
          // sinon la grille se calcule sur 0 et n'affiche aucun livre.
          width: rect.width > 0 ? rect.width : previous.width,
          height: Math.max(400, window.innerHeight - rect.top - 100) // Hauteur dynamique
        }));
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Charger l'historique de recherche depuis localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('calibre_search_history');
    if (savedHistory) {
      try {
        setSearchHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('Erreur chargement historique recherche:', e);
      }
    }
  }, []);

  // Sauvegarder l'historique de recherche
  const saveSearchToHistory = useCallback((query: string) => {
    if (!query.trim()) return;
    
    const newHistory = [
      query.trim(),
      ...searchHistory.filter(item => item !== query.trim())
    ].slice(0, 10); // Garder seulement les 10 dernières recherches
    
    setSearchHistory(newHistory);
    localStorage.setItem('calibre_search_history', JSON.stringify(newHistory));
  }, [searchHistory]);

  // Debounce pour la recherche instantanée
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      if (searchQuery.trim()) {
        saveSearchToHistory(searchQuery);
      }
    }, 300); // 300ms de délai

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, saveSearchToHistory]);

  // Générer les suggestions de recherche
  const searchSuggestions = useMemo(() => {
    if (!Array.isArray(contextBooks) || !contextBooks.length) return [];

    const suggestions = new Set();
    const query = debouncedQuery.toLowerCase();

    contextBooks.forEach(book => {
      // Suggestions basées sur le titre
      const title = book.title || book.file_original_name || '';
      if (title.toLowerCase().includes(query) && query.length > 0) {
        suggestions.add(title);
      }

      // Suggestions basées sur l'auteur
      if (book.author && book.author.toLowerCase().includes(query) && query.length > 0) {
        suggestions.add(book.author);
      }

      // Suggestions basées sur les tags
      if (book.tags_json) {
        try {
          const tags = JSON.parse(book.tags_json);
          tags.forEach((tag: string) => {
            if (tag.toLowerCase().includes(query) && query.length > 0) {
              suggestions.add(tag);
            }
          });
        } catch (e) {
          // Ignorer les erreurs de parsing JSON
        }
      }
    });

    return Array.from(suggestions).slice(0, 8); // Limiter à 8 suggestions
  }, [contextBooks, debouncedQuery]);

  // Générer les recherches populaires basées sur les données
  const generatePopularSearches = useMemo(() => {
    if (!Array.isArray(contextBooks) || !contextBooks.length) return [];

    const popular: string[] = [];

    // Auteurs les plus fréquents
    const authorCount: Record<string, number> = {};
    contextBooks.forEach((book: BookItem) => {
      if (book.author) {
        authorCount[book.author] = (authorCount[book.author] || 0) + 1;
      }
    });

    const topAuthors = Object.entries(authorCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([author]) => author);

    popular.push(...topAuthors);

    // Tags les plus fréquents
    const tagCount: Record<string, number> = {};
    contextBooks.forEach((book: BookItem) => {
      if (book.tags_json) {
        try {
          const tags = JSON.parse(book.tags_json);
          tags.forEach((tag: string) => {
            tagCount[tag] = (tagCount[tag] || 0) + 1;
          });
        } catch (e) {
          // Ignorer les erreurs de parsing JSON
        }
      }
    });

    const topTags = Object.entries(tagCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([tag]) => tag);
    
    popular.push(...topTags);

    return popular.slice(0, 6); // Limiter à 6 éléments
  }, [contextBooks]);

  // Utiliser uniquement les données du contexte - pas de chargement local
  // Le AppContext charge déjà les livres au démarrage

  // Supprimé loadBooksLocal - utilisation du AppContext uniquement

  const isSearching = searchState.status === 'loading';

  /** Au moins un critère envoyé au serveur. */
  const hasSearchCriteria =
    debouncedQuery.trim() !== '' || Object.values(searchFilters).some(isMeaningfulFilter);

  /** Au moins un filtre appliqué localement à la liste. */
  const hasLocalFilter =
    authorFilter !== 'all' || tagFilter !== 'all' || formatFilter !== 'all' || statusFilter !== 'all';

  // Base de travail UNIQUE de l'écran : résultats du serveur si une recherche est
  // en cours, bibliothèque du contexte sinon. Un succès à zéro élément reste vide
  // (audit BIB-002) et une erreur ne présente jamais la bibliothèque complète
  // comme résultat de la requête échouée (audit BIB-007).
  const baseBooks = useMemo<BookItem[]>(() => {
    if (searchState.status === 'success') return searchState.items;
    if (searchState.status === 'error' || searchState.status === 'loading') return [];
    return Array.isArray(contextBooks) ? contextBooks : [];
  }, [searchState, contextBooks]);

  // Filtrage et tri appliqués à la collection réellement rendue : c'est la même
  // liste qui alimente l'affichage, le compteur et les filtres actifs
  // (audit BIB-003 / BIB-006). La recherche textuelle n'est plus refiltrée ici :
  // le serveur l'a déjà faite, et un second filtrage lexical viderait les
  // résultats des modes sémantique et hybride.
  const visibleBooks = useMemo(() => {
    let filtered = baseBooks;

    // Filtres de base
    if (statusFilter !== 'all') {
      filtered = filtered.filter(book => book.status === statusFilter);
    }

    if (authorFilter !== 'all') {
      filtered = filtered.filter(book => book.author === authorFilter);
    }

    // Filtres multi-facettes
    if (tagFilter !== 'all') {
      filtered = filtered.filter(book => {
        if (!book.tags_json) return false;
        try {
          const tags = JSON.parse(book.tags_json);
          return Array.isArray(tags) && tags.some(tag => tag.toLowerCase().includes(tagFilter.toLowerCase()));
        } catch {
          return false;
        }
      });
    }

    if (formatFilter !== 'all') {
      filtered = filtered.filter(book => {
        const filename = book.file_original_name || '';
        return filename.toLowerCase().includes(`.${formatFilter.toLowerCase()}`);
      });
    }

    // Filtres avancés
    if (languageFilter !== 'all') {
      filtered = filtered.filter(book => book.lang === languageFilter);
    }

    if (seriesFilter.trim()) {
      const series = seriesFilter.toLowerCase();
      filtered = filtered.filter(book => 
        (book.series || '').toLowerCase().includes(series)
      );
    }

    if (yearFromFilter.trim()) {
      const yearFrom = parseInt(yearFromFilter);
      if (!isNaN(yearFrom)) {
        filtered = filtered.filter(book => book.year && book.year >= yearFrom);
      }
    }

    if (yearToFilter.trim()) {
      const yearTo = parseInt(yearToFilter);
      if (!isNaN(yearTo)) {
        filtered = filtered.filter(book => book.year && book.year <= yearTo);
      }
    }

    if (genreFilter !== 'all') {
      filtered = filtered.filter(book => book.genre === genreFilter);
    }

    if (difficultyFilter !== 'all') {
      filtered = filtered.filter(book => book.difficulty === difficultyFilter);
    }

    if (publisherFilter.trim()) {
      const publisher = publisherFilter.toLowerCase();
      filtered = filtered.filter(book => 
        (book.publisher || '').toLowerCase().includes(publisher)
      );
    }

    if (notesFilter.trim()) {
      const notes = notesFilter.toLowerCase();
      filtered = filtered.filter(book => 
        (book.notes || '').toLowerCase().includes(notes)
      );
    }

    // Tri : sur une COPIE, sinon .sort() réordonne en place le tableau du
    // contexte ou des résultats de recherche.
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'title':
          aVal = (a.title || a.file_original_name || '').toLowerCase();
          bVal = (b.title || b.file_original_name || '').toLowerCase();
          break;
        case 'author':
          aVal = (a.author || '').toLowerCase();
          bVal = (b.author || '').toLowerCase();
          break;
        case 'created_at':
          aVal = a.created_at ?? 0;
          bVal = b.created_at ?? 0;
          break;
        case 'updated_at':
          aVal = a.updated_at ?? 0;
          bVal = b.updated_at ?? 0;
          break;
        case 'year':
          aVal = a.year || 0;
          bVal = b.year || 0;
          break;
        case 'series':
          aVal = (a.series || '').toLowerCase();
          bVal = (b.series || '').toLowerCase();
          break;
        case 'rating':
          // Colonne `rating` du livre (0 = non noté).
          aVal = a.rating ?? 0;
          bVal = b.rating ?? 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [baseBooks, statusFilter, authorFilter, tagFilter, formatFilter, languageFilter, seriesFilter, yearFromFilter, yearToFilter, genreFilter, difficultyFilter, publisherFilter, notesFilter, sortBy, sortOrder]);

  // Le callback d'action reste stable pour la liste virtualisée, mais il doit
  // voir la collection du rendu courant dès que celle-ci est affichée. Un
  // effet passif laissait une courte fenêtre où un clic pouvait encore lire
  // la collection du rendu précédent ; le layout effect ferme cette fenêtre
  // avant toute interaction utilisateur.
  useLayoutEffect(() => {
    visibleBooksRef.current = visibleBooks;
  }, [visibleBooks]);

  // Extraire les valeurs uniques pour les filtres
  const uniqueAuthors = useMemo(() => {
    const authors = [...new Set((Array.isArray(contextBooks) ? contextBooks : []).map(book => book.author).filter(Boolean))];
    return authors.sort();
  }, [contextBooks]);

  const uniqueLanguages = useMemo(() => {
    const languages = [...new Set((Array.isArray(contextBooks) ? contextBooks : []).map(book => book.lang).filter(Boolean))];
    return languages.sort();
  }, [contextBooks]);

  const uniqueGenres = useMemo(() => {
    const genres = [...new Set((Array.isArray(contextBooks) ? contextBooks : []).map(book => book.genre).filter(Boolean))];
    return genres.sort();
  }, [contextBooks]);

  // Gestionnaires pour la recherche
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSearchSuggestions(value.length > 0);
  };

  const handleSearchFocus = () => {
    setSearchFocused(true);
    setShowSearchSuggestions(searchQuery.length > 0 || searchHistory.length > 0);
  };

  const handleSearchBlur = () => {
    // Délai pour permettre le clic sur les suggestions
    setTimeout(() => {
      setSearchFocused(false);
      setShowSearchSuggestions(false);
    }, 200);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    setShowSearchSuggestions(false);
    searchInputRef.current?.focus();
  };

  const handleHistoryClick = (historyItem: string) => {
    setSearchQuery(historyItem);
    setShowSearchSuggestions(false);
    searchInputRef.current?.focus();
  };

  const handlePopularClick = (popularItem: string) => {
    setSearchQuery(popularItem);
    setShowSearchSuggestions(false);
    searchInputRef.current?.focus();
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchSuggestions(false);
    searchInputRef.current?.focus();
  };

  const clearAllFilters = () => {
    setAuthorFilter('all');
    setTagFilter('all');
    setFormatFilter('all');
    setStatusFilter('all');
    setSearchQuery('');
    setSearchFilters({
      language: 'all',
      series: '',
      yearFrom: '',
      yearTo: '',
      genre: 'all',
      difficulty: 'all',
      publisher: '',
      notes: '',
      readStatus: 'all',
      rating: 'all',
      fileType: 'all'
    });
  };

  const removeFromHistory = (historyItem: string) => {
    const newHistory = searchHistory.filter(item => item !== historyItem);
    setSearchHistory(newHistory);
    localStorage.setItem('calibre_search_history', JSON.stringify(newHistory));
  };

  const handleOpenFolder = async (bookId: number | string | undefined) => {
    try {
      const data = await apiJSON(`books/${bookId}/folder`);
      
      // Copier le chemin dans le presse-papier et afficher toast
      try {
        await navigator.clipboard.writeText(data.folder_path);
        notificationService.showToast(`📁 Chemin dossier copié: ${data.folder_path}`, 'success', 8000);
      } catch {
        // Fallback si clipboard API indisponible
                  notificationService.showToast(`📁 Dossier: ${data.folder_path}`, 'info', 10000);
      }
    } catch (error) {
              handleApiError(error, 'Ouverture dossier');
    }
  };

  const handleExportBook = async (bookId: number | string | undefined) => {
    // Toast de démarrage
            notificationService.showToast('📦 Préparation export...', 'info', 3000);
    
    try {
      const response = await apiFetch(`export/book/${bookId}`);
      
      if (response.ok) {
        // Déclencher téléchargement
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `livre_${bookId}_export.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        notificationService.showToast('✅ Export ZIP téléchargé avec succès !', 'success');
      } else {
        const error = await response.json();
        handleApiError(new Error(error.detail || 'Export échoué'), 'Export');
      }
    } catch (error) {
              handleApiError(error, 'Export');
    }
  };



  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'indexed':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'received':
      case 'processing':
        return <Loader size={16} className="text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getStatusLabel = (status?: string) => {
    const labels: Record<string, string> = {
      'indexed': 'Indexé',
      'received': 'Reçu',
      'processing': 'En cours',
      'error': 'Erreur'
    };
    return (status && labels[status]) || status;
  };

  const formatDate = (timestamp?: number) => {
    return new Date((timestamp ?? 0) * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const BookCard = ({ book }: { book: BookItem }) => (
    <div className="book-card">
      <BookCover
        book={book}
        size="medium"
        showStatus={true}
        className="book-card-cover"
        onClick={((b: BookItem) => handleBookAction(b.book_id, 'read')) as any}
      />

      <div className="book-info">
        <div className="book-title">
          {book.title || book.file_original_name}
        </div>
        
        {book.author && (
          <div className="book-author">
            <User size={16} />
            <span>{book.author}</span>
          </div>
        )}

        <div className="book-meta">
          <div className="book-meta-item">
            <Calendar size={16} />
            <span>{formatDate(book.created_at)}</span>
          </div>
          
          {book.tags_json && (() => { try { const tags = JSON.parse(book.tags_json); return tags.length > 0; } catch { return false; } })() && (
            <div className="book-tags">
              {(() => { try { return JSON.parse(book.tags_json).slice(0, 2); } catch { return []; } })().map((tag: string, index: number) => (
                <span key={index} className="book-tag">
                  <Tag size={16} />
                  {tag}
                </span>
              ))}
              {(() => { try { return JSON.parse(book.tags_json).length > 2; } catch { return false; } })() && (
                <span className="book-tag-more">
                  +{(() => { try { return JSON.parse(book.tags_json).length - 2; } catch { return 0; } })()}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="book-actions">
          <button
            className="book-action primary"
            onClick={() => handleBookAction(book.book_id, 'read')}
            title="Lire"
          >
            <Eye size={16} />
            Lire
          </button>
          
          <button
            className="book-action"
            onClick={() => handleBookAction(book.book_id, 'ask')}
            title="Interroger avec l'IA"
          >
            <FileText size={16} />
            IA
          </button>
          
          <button
            className="book-action koreader"
            onClick={() => handleBookAction(book.book_id, 'koreader')}
            title="Ouvrir avec KOReader"
          >
            <ExternalLink size={16} />
            KOReader
          </button>

          <div className="book-action-menu">
            <button className="book-action-menu-trigger">
              <MoreVertical size={16} />
            </button>
            <div className="book-action-menu-content">
              <button onClick={() => handleBookAction(book.book_id, 'edit')}>
                <Edit size={16} />
                Modifier
              </button>
              <button onClick={() => handleBookAction(book.book_id, 'reader')}>
                <BookOpen size={16} />
                Lecteur Avancé
              </button>
              <button onClick={() => handleBookAction(book.book_id, 'koreader')}>
                <Book size={16} />
                KOReader
              </button>
              <button onClick={() => handleBookAction(book.book_id, 'folder')}>
                <ExternalLink size={16} />
                Dossier
              </button>
              <button onClick={() => handleBookAction(book.book_id, 'export')}>
                <Download size={16} />
                Exporter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const BookListItem = ({ book }: { book: BookItem }) => (
    <div className="book-list-item">
      <BookCover
        book={book}
        size="small"
        showStatus={true}
        className="book-list-cover"
        onClick={((b: BookItem) => handleBookAction(b.book_id, 'read')) as any}
      />

      <div className="book-list-info">
        <div className="book-list-main">
          <div className="book-list-title">
            {book.title || book.file_original_name}
          </div>
          {book.author && (
            <div className="book-list-author">{book.author}</div>
          )}
        </div>

        <div className="book-list-meta">
          <div className="book-list-status">
            {getStatusIcon(book.status)}
            <span>{getStatusLabel(book.status)}</span>
          </div>
          
          <div className="book-list-date">
            {formatDate(book.created_at)}
          </div>
        </div>

        <div className="book-list-tags">
          {book.tags_json && (() => { try { return JSON.parse(book.tags_json); } catch { return []; } })().map((tag: string, index: number) => (
            <span key={index} className="book-list-tag">{tag}</span>
          ))}
        </div>
      </div>

      <div className="book-list-actions">
        <button
          className="book-action primary"
          onClick={() => handleBookAction(book.book_id, 'read')}
        >
          <Eye size={16} />
        </button>
        
        <button
          className="book-action"
          onClick={() => handleBookAction(book.book_id, 'reader')}
          title="Lecteur Avancé"
        >
          <BookOpen size={16} />
        </button>
        
        <button
          className="book-action"
          onClick={() => handleBookAction(book.book_id, 'koreader')}
          title="Ouvrir avec KOReader"
        >
          <Book size={16} />
        </button>
        
        <button
          className="book-action"
          onClick={() => handleBookAction(book.book_id, 'ask')}
        >
          <FileText size={16} />
        </button>
        
        <button
          className="book-action"
          onClick={() => handleBookAction(book.book_id, 'edit')}
          title="Modifier les métadonnées"
        >
          <Edit size={16} />
        </button>
      </div>
    </div>
  );

  const BookCardCompact = ({ book }: { book: BookItem }) => (
    <div className="book-card-compact">
      <div className="book-card-cover">
        <div className="book-card-cover-placeholder">
          <Book size={24} />
        </div>
        <div className="book-card-status">
          {getStatusIcon(book.status)}
        </div>
      </div>

      <div className="book-card-content">
        <div className="book-card-title">
          {book.title || book.file_original_name}
        </div>
        
        {book.author && (
          <div className="book-card-author">
            <User size={16} />
            <span>{book.author}</span>
          </div>
        )}

        <div className="book-card-meta">
          <div className="book-card-date">
            <Calendar size={16} />
            <span>{formatDate(book.created_at)}</span>
          </div>
          
          {book.year && (
            <div className="book-card-year">
              <span>{book.year}</span>
            </div>
          )}
        </div>

        <div className="book-card-actions">
          <button
            className="book-action primary"
            onClick={() => handleBookAction(book.book_id, 'read')}
            aria-label="Lire le livre"
          >
            <Eye size={16} />
            <span className="sr-only">Lire</span>
          </button>
          
          <button
            className="book-action"
            onClick={() => handleBookAction(book.book_id, 'koreader')}
            aria-label="Ouvrir avec KOReader"
            title="KOReader"
          >
            <Book size={16} />
            <span className="sr-only">KOReader</span>
          </button>
          
          <button
            className="book-action"
            onClick={() => handleBookAction(book.book_id, 'ask')}
            aria-label="Interroger avec l'IA"
          >
            <FileText size={16} />
            <span className="sr-only">IA</span>
          </button>
          
          <button
            className="book-action"
            onClick={() => handleBookAction(book.book_id, 'edit')}
            aria-label="Modifier les métadonnées"
            title="Modifier"
          >
            <Edit size={16} />
            <span className="sr-only">Modifier</span>
          </button>
        </div>
      </div>
    </div>
  );

  const BookTimelineItem = ({ book }: { book: BookItem }) => (
    <div className="book-timeline-item">
      <div className="book-timeline-date">
        <div className="book-timeline-day">
          {new Date((book.created_at ?? 0) * 1000).getDate()}
        </div>
        <div className="book-timeline-month">
          {new Date((book.created_at ?? 0) * 1000).toLocaleDateString('fr-FR', { month: 'short' })}
        </div>
      </div>

      <div className="book-timeline-content">
        <div className="book-timeline-header">
          <div className="book-timeline-title">
            {book.title || book.file_original_name}
          </div>
          <div className="book-timeline-status">
            {getStatusIcon(book.status)}
          </div>
        </div>

        {book.author && (
          <div className="book-timeline-author">
            <User size={16} />
            <span>{book.author}</span>
          </div>
        )}

        <div className="book-timeline-meta">
          {book.series && (
            <span className="book-timeline-series">{book.series}</span>
          )}
          {book.year && (
            <span className="book-timeline-year">{book.year}</span>
          )}
          {book.lang && (
            <span className="book-timeline-lang">{book.lang}</span>
          )}
        </div>

        <div className="book-timeline-actions">
          <button
            className="book-action primary"
            onClick={() => handleBookAction(book.book_id, 'read')}
            aria-label="Lire le livre"
          >
            <Eye size={16} />
            Lire
          </button>
          
          <button
            className="book-action"
            onClick={() => handleBookAction(book.book_id, 'koreader')}
            aria-label="Ouvrir avec KOReader"
          >
            <Book size={16} />
            KOReader
          </button>
          
          <button
            className="book-action"
            onClick={() => handleBookAction(book.book_id, 'ask')}
            aria-label="Interroger avec l'IA"
          >
            <FileText size={16} />
            IA
          </button>
          
          <button
            className="book-action"
            onClick={() => handleBookAction(book.book_id, 'edit')}
            aria-label="Modifier les métadonnées"
          >
            <Edit size={16} />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );

  if (contextLoading) {
    return (
      <div className="bibliotheque-loading">
        <Loader size={48} className="animate-spin" />
        <p>Chargement de votre bibliothèque...</p>
      </div>
    );
  }

  // Échec du chargement initial : sans cette branche, l'écran restait sur le
  // spinner indéfiniment, sans message ni moyen de relancer.
  if (contextError && (!Array.isArray(contextBooks) || contextBooks.length === 0)) {
    return (
      <div className="search-error" role="alert">
        <AlertCircle size={48} />
        <p className="search-error-message">
          Impossible de charger votre bibliothèque : {contextError}
        </p>
        <p className="search-error-hint">Le serveur est peut-être indisponible.</p>
        <button onClick={() => loadBooks()} className="upload-btn">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="bibliotheque app-page">
      {/* Header avec recherche */}
      <div className="bibliotheque-header">
        <div className="search-section">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Rechercher dans votre bibliothèque..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowSearchSuggestions(true)}
              className="search-input"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="clear-search-btn"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Les boutons Texte / Sémantique / Hybride ont été retirés : le
              paramètre `search_type` n'était pas exploité par /api/books/search,
              les trois modes exécutaient la même requête. La recherche
              sémantique et hybride reste disponible sur l'écran Recherche, dont
              c'est l'endpoint dédié (/api/search, mode=fts|vector|hybrid). */}

          {/* Suggestions de recherche */}
          {showSearchSuggestions && intelligentSuggestions.length > 0 && (
            <div className="search-suggestions">
              {intelligentSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSearchQuery(suggestion.split(': ')[1] || suggestion);
                    setShowSearchSuggestions(false);
                  }}
                  className="suggestion-item"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Contrôles de vue et filtres */}
        <div className="bibliotheque-controls">
          <div className="view-controls">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <Grid size={20} />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={20} />
            </button>
            <button
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              <Book size={20} />
            </button>
          </div>

          <div className="filter-controls">
            {/* Filtres de base */}
            <select
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              className="filter-select"
              aria-label="Filtrer par auteur"
              disabled={loadingFilterValues}
            >
              <option value="all">
                {loadingFilterValues ? 'Chargement...' : 'Tous les auteurs'}
              </option>
              {filterValues.authors.map((author, index) => (
                <option key={index} value={author}>{author}</option>
              ))}
            </select>

            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="filter-select"
              aria-label="Filtrer par tag"
              disabled={loadingFilterValues}
            >
              <option value="all">
                {loadingFilterValues ? 'Chargement...' : 'Tous les tags'}
              </option>
              {filterValues.tags.map((tag, index) => (
                <option key={index} value={tag}>{tag}</option>
              ))}
            </select>

            <select
              value={formatFilter}
              onChange={(e) => setFormatFilter(e.target.value)}
              className="filter-select"
              aria-label="Filtrer par format"
              disabled={loadingFilterValues}
            >
              <option value="all">
                {loadingFilterValues ? 'Chargement...' : 'Tous les formats'}
              </option>
              {filterValues.formats.map((format, index) => (
                <option key={index} value={format}>{format.toUpperCase()}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
              aria-label="Filtrer par statut"
            >
              <option value="all">Tous les statuts</option>
              <option value="indexed">Indexé</option>
              <option value="received">Reçu</option>
              <option value="processing">En cours</option>
              <option value="error">Erreur</option>
            </select>

            <button
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className="filter-toggle-btn"
            >
              <Filter size={20} />
              Filtres avancés
            </button>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
              aria-label="Trier par"
            >
              <option value="created_at">Date d'ajout</option>
              <option value="title">Titre</option>
              <option value="author">Auteur</option>
              <option value="series">Série</option>
              <option value="rating">Note</option>
              {/* « Taille » retiré : aucune colonne de taille de fichier n'existe
                  côté modèle Book ni en base, le tri était sans effet (BIB-004). */}
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="sort-order-btn"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>

            <button
              onClick={clearAllFilters}
              className="clear-filters-btn"
              title="Effacer tous les filtres"
            >
              <X size={16} />
            </button>

            <button
              onClick={handleShowQuiz}
              className="quiz-btn"
              title="Tester mes connaissances"
              disabled={!contextBooks || contextBooks.length === 0}
            >
              <Brain size={20} />
              Quiz
            </button>
          </div>
        </div>
      </div>

      {/* Filtres avancés */}
      {showAdvancedSearch && (
        <div className="advanced-filters">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Langue</label>
              <select
                value={searchFilters.language}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, language: e.target.value }))}
              >
                <option value="all">Toutes les langues</option>
                <option value="fr">Français</option>
                <option value="en">Anglais</option>
                <option value="es">Espagnol</option>
                <option value="de">Allemand</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Série</label>
              <input
                type="text"
                placeholder="Nom de la série"
                value={searchFilters.series}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, series: e.target.value }))}
              />
            </div>

            <div className="filter-group">
              <label>Année (de)</label>
              <input
                type="number"
                placeholder="1900"
                value={searchFilters.yearFrom}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, yearFrom: e.target.value }))}
              />
            </div>

            <div className="filter-group">
              <label>Année (à)</label>
              <input
                type="number"
                placeholder="2024"
                value={searchFilters.yearTo}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, yearTo: e.target.value }))}
              />
            </div>

            <div className="filter-group">
              <label>Genre</label>
              <select
                value={searchFilters.genre}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, genre: e.target.value }))}
              >
                <option value="all">Tous les genres</option>
                <option value="fiction">Fiction</option>
                <option value="non-fiction">Non-fiction</option>
                <option value="science-fiction">Science-fiction</option>
                <option value="fantasy">Fantasy</option>
                <option value="mystery">Mystère</option>
                <option value="romance">Romance</option>
                <option value="biography">Biographie</option>
                <option value="history">Histoire</option>
                <option value="science">Science</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Niveau de difficulté</label>
              <select
                value={searchFilters.difficulty}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, difficulty: e.target.value }))}
              >
                <option value="all">Tous niveaux</option>
                <option value="beginner">Débutant</option>
                <option value="intermediate">Intermédiaire</option>
                <option value="advanced">Avancé</option>
                <option value="expert">Expert</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Éditeur</label>
              <input
                type="text"
                placeholder="Nom de l'éditeur"
                value={searchFilters.publisher}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, publisher: e.target.value }))}
              />
            </div>

            <div className="filter-group">
              <label>Statut de lecture</label>
              <select
                value={searchFilters.readStatus}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, readStatus: e.target.value }))}
              >
                <option value="all">Tous les statuts</option>
                <option value="unread">Non lu</option>
                <option value="read">Lu</option>
                {/* « En cours » retiré : le statut se déduit de read_count côté
                    serveur (lu / non lu) et rien en base ne distingue une lecture
                    en cours. L'option ne filtrait rien. */}
              </select>
            </div>

            <div className="filter-group">
              <label>Note</label>
              <select
                value={searchFilters.rating}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, rating: e.target.value }))}
              >
                <option value="all">Toutes les notes</option>
                <option value="1-2">1-2 étoiles</option>
                <option value="3-4">3-4 étoiles</option>
                <option value="5">5 étoiles</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Type de fichier</label>
              <select
                value={searchFilters.fileType}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, fileType: e.target.value }))}
              >
                <option value="all">Tous les formats</option>
                <option value="pdf">PDF</option>
                <option value="epub">EPUB</option>
                <option value="txt">TXT</option>
              </select>
            </div>
          </div>

          <div className="filter-actions">
            <button
              onClick={() => setSearchFilters({
                language: 'all',
                series: '',
                yearFrom: '',
                yearTo: '',
                genre: 'all',
                difficulty: 'all',
                publisher: '',
                notes: '',
                readStatus: 'all',
                rating: 'all',
                fileType: 'all'
              })}
              className="clear-filters-btn"
            >
              Effacer tous les filtres
            </button>
          </div>
        </div>
      )}

      {/* Résultats de recherche */}
      <div className="bibliotheque-results">
        {isSearching ? (
          <LoadingStates.Loading message="Recherche en cours..." />
        ) : searchState.status === 'error' ? (
          /* État d'erreur distinct de « contenu » et de « aucun résultat » : la
             bibliothèque n'est pas affichée à la place du résultat (BIB-007). */
          <div className="search-error" role="alert">
            <AlertCircle size={48} />
            <p className="search-error-message">La recherche a échoué : {searchState.message}</p>
            <p className="search-error-hint">Aucun résultat n'est affiché pour cette requête.</p>
            <button onClick={() => setRetryToken((token) => token + 1)} className="upload-btn">
              Réessayer
            </button>
          </div>
        ) : visibleBooks.length === 0 ? (
          <LoadingStates.Empty
            message={hasSearchCriteria || hasLocalFilter
              ? "Aucun livre ne correspond à vos critères de recherche"
              : "Aucun livre dans votre bibliothèque"
            }
            action={(hasSearchCriteria || hasLocalFilter ? (
              <button onClick={clearAllFilters} className="upload-btn">
                Effacer les critères
              </button>
            ) : (
              <button onClick={() => navigate('/upload')} className="upload-btn">
                Importer votre premier livre
              </button>
            )) as any}
          />
        ) : (
          <>
            <div className="results-header">
              <span className="results-count">
                {visibleBooks.length} livre{visibleBooks.length > 1 ? 's' : ''} affiché{visibleBooks.length > 1 ? 's' : ''}
                {searchQuery && ` pour "${searchQuery}"`}
                {searchState.status === 'success' && searchState.total > searchState.items.length &&
                  ` — ${searchState.total} correspondances au total, ${SEARCH_PAGE_SIZE} premières chargées`}
              </span>

              {/* Indicateurs de filtres actifs */}
              {(authorFilter !== 'all' || tagFilter !== 'all' || formatFilter !== 'all' || statusFilter !== 'all') && (
                <div className="active-filters">
                  <span className="active-filters-label">Filtres actifs:</span>
                  {authorFilter !== 'all' && (
                    <span className="active-filter-tag">
                      Auteur: {authorFilter}
                      <button onClick={() => setAuthorFilter('all')} className="remove-filter-btn">
                        <X size={16} />
                      </button>
                    </span>
                  )}
                  {tagFilter !== 'all' && (
                    <span className="active-filter-tag">
                      Tag: {tagFilter}
                      <button onClick={() => setTagFilter('all')} className="remove-filter-btn">
                        <X size={16} />
                      </button>
                    </span>
                  )}
                  {formatFilter !== 'all' && (
                    <span className="active-filter-tag">
                      Format: {formatFilter.toUpperCase()}
                      <button onClick={() => setFormatFilter('all')} className="remove-filter-btn">
                        <X size={16} />
                      </button>
                    </span>
                  )}
                  {statusFilter !== 'all' && (
                    <span className="active-filter-tag">
                      Statut: {statusFilter}
                      <button onClick={() => setStatusFilter('all')} className="remove-filter-btn">
                        <X size={16} />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>

            <div 
              ref={containerRef}
              className={`books-container view-${viewMode}`}
              style={{ height: containerDimensions.height }}
            >
              {visibleBooks.length > 0 ? (
                <VirtualizedBookList
                  books={visibleBooks}
                  viewMode={viewMode}
                  onBookAction={handleBookAction}
                  containerHeight={containerDimensions.height}
                  containerWidth={containerDimensions.width}
                />
              ) : (
                <div className="no-books-message">
                  <Book size={48} />
                  <p>Aucun livre trouvé</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal d'édition des métadonnées */}
      <EditBookModal
        book={editingBook}
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdit}
      />

      {/* Générateur de Quiz Adaptatif */}
      {showQuiz && (
        <QuizView
          selectedBooks={selectedBooksForQuiz}
          onClose={handleCloseQuiz}
        />
      )}

      <style>{`
        .bibliotheque {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
        }
        
        .bibliotheque-header {
          margin-bottom: 24px;
        }
        
        .search-section {
          position: relative;
          margin-bottom: 16px;
        }
        
        .search-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .search-icon {
          position: absolute;
          left: 12px;
          color: #6b7280;
        }
        
        .search-input {
          width: 100%;
          padding: 12px 40px 12px 40px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #8B5CF6;
        }
        
        .clear-search-btn {
          position: absolute;
          right: 12px;
          padding: 4px;
          border: none;
          background: none;
          cursor: pointer;
          color: #6b7280;
        }
        
        .search-type-selector {
          display: flex;
          gap: 8px;
          margin-top: 12px;
        }
        
        .search-type-btn {
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .search-type-btn.active {
          background: #8B5CF6;
          color: white;
          border-color: #8B5CF6;
        }
        
        .search-suggestions {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          z-index: 10;
          max-height: 300px;
          overflow-y: auto;
        }
        
        .suggestion-item {
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          font-size: 14px;
        }
        
        .suggestion-item:hover {
          background: #f3f4f6;
        }
        
        .bibliotheque-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }
        
        .view-controls {
          display: flex;
          gap: 8px;
        }
        
        .view-btn {
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          color: #6b7280;
        }
        
        .view-btn.active {
          background: #8B5CF6;
          color: white;
          border-color: #8B5CF6;
        }
        
        .filter-controls {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        
        .filter-toggle-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          font-size: 14px;
        }
        
        .sort-select {
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          font-size: 14px;
        }
        
        .sort-order-btn {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          font-size: 16px;
          font-weight: bold;
        }
        
        .filter-select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          font-size: 14px;
          min-width: 120px;
        }
        
        .filter-select:disabled {
          background: #f9fafb;
          color: #9ca3af;
          cursor: not-allowed;
        }
        
        .clear-filters-btn {
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .clear-filters-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }
        
        .advanced-filters {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 24px;
        }
        
        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }
        
        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .filter-group label {
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }
        
        .filter-group input,
        .filter-group select {
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .filter-actions {
          margin-top: 16px;
          text-align: center;
        }
        
        .clear-filters-btn {
          padding: 8px 16px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          font-size: 14px;
        }
        
        .bibliotheque-results {
          min-height: 400px;
        }
        
        .results-header {
          margin-bottom: 16px;
        }

        .search-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 48px 24px;
          color: #b91c1c;
          text-align: center;
        }

        .search-error-message {
          font-weight: 600;
        }

        .search-error-hint {
          font-size: 14px;
          color: #6b7280;
        }
        
        .results-count {
          font-size: 14px;
          color: #6b7280;
        }
        
        .active-filters {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
          flex-wrap: wrap;
        }
        
        .active-filters-label {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }
        
        .active-filter-tag {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: #e5e7eb;
          border-radius: 12px;
          font-size: 12px;
          color: #374151;
        }
        
        .remove-filter-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2px;
          border: none;
          background: none;
          cursor: pointer;
          color: #6b7280;
          border-radius: 50%;
        }
        
        .remove-filter-btn:hover {
          background: #d1d5db;
          color: #374151;
        }
        
        .books-grid {
          display: grid;
          gap: 20px;
        }
        
        .books-grid.view-grid {
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        }
        
        .books-grid.view-list {
          grid-template-columns: 1fr;
        }
        
        .books-grid.view-cards {
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        }
        
        .upload-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          background: #8B5CF6;
          color: white;
          cursor: pointer;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default Bibliotheque;
